import { Box, Stack, Typography } from "@mui/material"
import React, { FC } from "react"
import { Enum_Trend_Direction_Enum } from "../../generated/graphql"
import { checkTimeForHours } from "../../utils/helpers"
import { TrendData } from "../../utils/types"
import ArrowDownIcon from "../atoms/ArrowDownIcon"
import ArrowNeutralIcon from "../atoms/ArrowNeutralIcon"
import ArrowUpIcon from "../atoms/ArrowUpIcon"

const DailyAverage: FC<{
  label: string
  value?: string
  unit?: string
  direction: Enum_Trend_Direction_Enum
  border?: boolean
}> = ({ label, value, unit, direction, border }) => {
  const { isHour, hour, minute } = checkTimeForHours(value, unit)

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={(theme) => ({
        height: 24,
        ml: 1.5,
        pl: 1.5,
        minWidth: 0,
        borderLeft: border
          ? `1px solid ${theme.palette.vira?.border ?? theme.palette.divider}`
          : "none",
      })}
    >
      <Typography
        variant="body2"
        sx={(theme) => ({
          color: theme.palette.vira?.ink ?? theme.palette.text.primary,
          mr: 2,
          fontWeight: 400,
          fontSize: 12,
        })}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center">
        <Stack direction="row" sx={{ mr: 1.5, textAlign: "right" }}>
          {!isHour ? (
            <>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.vira?.ink ?? theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: 12,
                })}
              >
                {value && value !== "" ? value : "--"}
              </Typography>
              {unit ? (
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color:
                      theme.palette.vira?.ink ?? theme.palette.text.primary,
                    fontWeight: 400,
                    fontSize: 12,
                    ml: unit === "min" ? 0.5 : 0,
                  })}
                >
                  {unit}
                </Typography>
              ) : null}
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.vira?.ink ?? theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: 12,
                })}
              >
                {hour}
              </Typography>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.vira?.ink ?? theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: 12,
                  ml: 0.5,
                })}
              >
                hr
              </Typography>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.vira?.ink ?? theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: 12,
                  ml: 0.5,
                })}
              >
                {minute}
              </Typography>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.vira?.ink ?? theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: 12,
                  ml: 0.5,
                })}
              >
                min
              </Typography>
            </>
          )}
        </Stack>
        {!value || value === "" ? (
          <Box sx={{ height: 24, width: 24 }} />
        ) : direction === Enum_Trend_Direction_Enum.Up ? (
          <ArrowUpIcon />
        ) : direction === Enum_Trend_Direction_Enum.Down ? (
          <ArrowDownIcon />
        ) : direction === Enum_Trend_Direction_Enum.NoChange ? (
          <ArrowNeutralIcon />
        ) : null}
      </Stack>
    </Stack>
  )
}

const WeeklyAverages: FC<{
  data: TrendData[]
}> = ({ data }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: (theme) => theme.spacing(1, 0),
        minWidth: 0,
        flex: "1 1 auto",
      }}
    >
      <Typography
        sx={(theme) => ({
          color: theme.palette.vira?.ink ?? theme.palette.text.primary,
          flex: "0 0 auto",
          mr: 1.5,
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          fontSize: 15,
          letterSpacing: "-0.15px",
        })}
      >
        Weekly Averages
      </Typography>
      {data.map(({ label, value, unit, direction }, index, array) => (
        <DailyAverage
          key={label}
          label={label}
          value={value}
          unit={unit}
          direction={direction}
          border={index < array.length}
        />
      ))}
    </Box>
  )
}

export default WeeklyAverages
